import { useEffect, useRef } from 'react';
import { Button } from './Button';
import { Heading } from './Heading';
import { Stack } from './Stack';
import { Text } from './Text';

/** Props for {@link ConfirmDialog}. */
export interface ConfirmDialogProps {
  /** Whether the dialog is showing. */
  readonly open: boolean;
  /** What the user is being asked to confirm. */
  readonly title: string;
  /** The consequence, stated plainly. */
  readonly message: string;
  /** Label for the confirming action. */
  readonly confirmLabel?: string;
  /** Whether the action destroys data, which styles the confirm button accordingly. */
  readonly destructive?: boolean;
  /** Invoked when the user confirms. */
  readonly onConfirm: () => void;
  /** Invoked when the user cancels or dismisses. */
  readonly onCancel: () => void;
}

/**
 * A modal confirmation built on the native `dialog` element.
 *
 * `showModal` brings focus trapping, Escape-to-dismiss, inertness of the page behind and the
 * top-layer stacking with it. Reimplementing those by hand is where hand-rolled modals usually go
 * wrong, so the platform does it instead.
 *
 * @param props - Component props.
 * @returns The dialog element.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialog === null) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialog === null) {
      return;
    }

    // Escape closes the dialog natively without telling React, so the cancel path is wired to the
    // element's own close event rather than only to the button.
    const handleClose = (): void => onCancel();
    dialog.addEventListener('close', handleClose);

    return () => dialog.removeEventListener('close', handleClose);
  }, [onCancel]);

  return (
    <dialog ref={dialogRef} className="gm-dialog" aria-label={title}>
      <Stack gap="lg">
        <Heading level={2} size="lg">
          {title}
        </Heading>
        <Text muted>{message}</Text>
        <Stack direction="row" gap="sm" justify="flex-end" wrap>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </Stack>
      </Stack>
    </dialog>
  );
}
